import * as React from "react";
import { LevelThreshold } from "../core/models/LevelThreshold";
import { intervalToDuration, isAfter } from "date-fns";
import addTime, { addTimeValidUnits, localeFormatDuration } from "./datetime";
import { EMPTY_STRING_LABEL } from "../core/constants/appGlobals";
import {
  installationLevelStatusString,
  CRITICAL_LEVEL_STATUS,
  ALERT_LEVEL_STATUS,
  levelStatus,
  OK_LEVEL_STATUS,
  INSTALLATION_LEVEL_STATUS_MAP,
} from "../core/models/LevelStatus";
import { TRIP_STATUS_IN_PROGRESS, TRIP_STATUS_PLANNED } from "../core/models/TripStatus";
import { LEVEL_COLOR_VERY_LOW, LEVEL_COLOR_LOW, LEVEL_COLOR_NORMAL } from "../RTUThemeProvider";
import { isArrayValid, isNumberValid } from "./utility";
import { makeStyles, Theme, createStyles, List, ListItem, ListItemText } from "@material-ui/core";
import TankModel from "../core/models/Tank";

export function calculateLevelPercentage(currentLevel?: number, capacity?: number): number {
  if (!capacity || !currentLevel) {
    return 0;
  }

  if (currentLevel && capacity && currentLevel >= capacity) {
    return 100;
  }

  return Math.round((currentLevel / capacity) * 100);
}

export function getLevelColor(
  value: number | levelStatus | undefined,
  thresholds?: LevelThreshold
): React.CSSProperties["color"] {
  if (value === undefined || value === null) {
    return "";
  }

  if (
    value === CRITICAL_LEVEL_STATUS ||
    (typeof value === "number" && thresholds?.veryLow && value <= thresholds?.veryLow)
  ) {
    return LEVEL_COLOR_VERY_LOW || "";
  }
  if (
    value === ALERT_LEVEL_STATUS ||
    (typeof value === "number" && thresholds?.low && value <= thresholds?.low)
  ) {
    return LEVEL_COLOR_LOW || "";
  }

  return LEVEL_COLOR_NORMAL || "";
}

export function getLevelStatusString(
  value?: number | levelStatus,
  thresholds?: LevelThreshold
): installationLevelStatusString | typeof EMPTY_STRING_LABEL {
  let currentStatus: undefined | number | "" | levelStatus = value;
  if (typeof value === "number" && thresholds) {
    currentStatus = getLevelStatus(value, thresholds);
  }

  if (!currentStatus) {
    return EMPTY_STRING_LABEL;
  }

  return INSTALLATION_LEVEL_STATUS_MAP[currentStatus as levelStatus];
}

export function getLevelStatus(value?: number, thresholds?: LevelThreshold): levelStatus | "" {
  if (!isNumberValid(value) || !thresholds) {
    return "";
  }

  /* eslint-disable @typescript-eslint/no-non-null-assertion */
  if (thresholds?.veryLow && value! <= thresholds?.veryLow) {
    return CRITICAL_LEVEL_STATUS;
  }
  if (thresholds?.low && value! <= thresholds?.low) {
    return ALERT_LEVEL_STATUS;
  }
  /* eslint-enable @typescript-eslint/no-non-null-assertion */

  return OK_LEVEL_STATUS;
}

export function checkForLevelDataDelay(
  date?: Date | string,
  delta = 6,
  unitOfTime: addTimeValidUnits = "h"
): boolean {
  if (!date) {
    return false;
  }

  const now = new Date();

  return isAfter(
    addTime(now, -delta, unitOfTime),
    new Date(new Date(date).getTime() + new Date(date).getTimezoneOffset() * 60000)
  );
}

export function checkTripInProgress(value: string | undefined): boolean {
  if (!value) {
    return false;
  }
  return value.trim().toLocaleLowerCase() === TRIP_STATUS_IN_PROGRESS.trim().toLowerCase();
}

export function checkTripPlanned(value: string | undefined) {
  if (!value) {
    return false;
  }
  return value.trim().toLocaleLowerCase() === TRIP_STATUS_PLANNED.trim().toLowerCase();
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    warningsWrapper: {
      flex: 1,
    },

    errorWrapper: {
      display: "flex",
    },

    error: {
      color: theme.palette.error.main,
    },

    listItem: {
      "justifyContent": "space-between",
      "paddingRight": theme.spacing(1),

      "& .MuiListItemText-root": {
        flex: "1 0",
      },

      "& > div": {
        marginLeft: theme.spacing(1),
      },
    },
  })
);

export function BuildDelayErrorTooltip(currentInterval: Interval) {
  const classes = useStyles();
  return (
    <div>
      <span className={classes.error}>
        {localeFormatDuration(intervalToDuration(currentInterval))}
      </span>
      <span> desde a última leitura</span>
    </div>
  );
}

export type RenderInstallationTankWarningsTooltipParams = {
  veryLowLevel?: number;
  lowLevel?: number;
  capacity?: number;
  lastReadingDate?: Date;
  levelStatus?: levelStatus;
  level?: number;
};

export function RenderInstallationTankWarningsTooltip(
  tankInstallation: RenderInstallationTankWarningsTooltipParams,
  nowDate: Date
) {
  const classes = useStyles();

  const thresholds: LevelThreshold = {
    veryLow: tankInstallation.veryLowLevel,
    low: tankInstallation.lowLevel,
    over: tankInstallation.capacity,
  };

  const delay = checkForLevelDataDelay(tankInstallation.lastReadingDate);

  const lateMeasuresMessage =
    delay && tankInstallation.lastReadingDate
      ? BuildDelayErrorTooltip({
          start: new Date(tankInstallation.lastReadingDate),
          end: nowDate,
        })
      : "";

  if (!lateMeasuresMessage && tankInstallation.levelStatus === OK_LEVEL_STATUS) {
    return null;
  }

  return (
    <div className={classes.warningsWrapper}>
      {tankInstallation.levelStatus !== OK_LEVEL_STATUS && (
        <div className={classes.errorWrapper}>
          <span>Nível: </span>
          <span
            style={{
              color: getLevelColor(
                tankInstallation.levelStatus || tankInstallation.level,
                thresholds
              ),
            }}
          >
            {` ${getLevelStatusString(
              tankInstallation.levelStatus || CRITICAL_LEVEL_STATUS,
              thresholds
            )}`}
            {`(${calculateLevelPercentage(
              tankInstallation.level,
              tankInstallation.capacity || 0
            )}%)`}
          </span>
          <br />
        </div>
      )}
      <div>{lateMeasuresMessage}</div>
    </div>
  );
}

export function RenderTanksWarnings(tanks: TankModel[] = [], nowDate: Date) {
  const classes = useStyles();

  if (!tanks) {
    return null;
  }

  const tanksWarnings = tanks?.map((tank) => {
      const warnings = RenderInstallationTankWarningsTooltip(
        {
          lastReadingDate: tank.last_rtu_reading_date
            ? new Date(
                new Date(tank.last_rtu_reading_date).getTime() +
                  new Date(tank.last_rtu_reading_date).getTimezoneOffset() * 60000
              )
            : undefined,
          level: tank.tank_level_su,
          levelStatus: tank.tank_level_status,
          capacity: tank.tank_capacity_su,
          veryLowLevel: tank.outage_level,
        },
        nowDate
      );
      if (!warnings) {
        return null;
      }
      return (
        <ListItem className={classes.listItem} key={tank.tank_id}>
          <ListItemText>
            <strong>{`Tanque ${tank.tank_id}`}</strong>
          </ListItemText>
          {warnings}
        </ListItem>
      );
    })
    .filter((x) => x);

  if (!isArrayValid(tanksWarnings)) {
    return null;
  }

  return <List>{tanksWarnings}</List>;
}
